<template>
  <div class="form-card" @click="openFunnel">
    <h2 class="form-card__title">
      {{ form.name }}
    </h2>
    <div class="form-card__subtitle">
      {{ formatDate(form.created_at) }}
    </div>
    <div class="form-card__type">
      <Label :variant="isConfigurator ? 'primary' : 'default'" size="sm">
        {{ isConfigurator ? $t('form_card.type_configurator') : $t('form_card.type_funnel') }}
      </Label>
    </div>
    <div class="form-card__actions">
      <DropdownMenu @click.stop.prevent>
        <template #trigger>
          <Btn variant="plain" :disabled="!isAdmin">
            <BaseIcon name="dots" size="18" />
          </Btn>
        </template>
        <template #items>
          <div class="dropdown-menu__item" @click.stop="router.push(`/flow/${form.id}`)">
            {{ $t('form_card.edit_funnel') }}
          </div>
          <div class="dropdown-menu__item" @click.stop="openFunnelMode('normal')">
            {{ $t('form_card.open_normal') }}
          </div>
          <div class="dropdown-menu__item" @click.stop="openFunnelMode('extended')">
            {{ $t('form_card.open_extended') }}
          </div>
          <div class="dropdown-menu__item" @click.stop="copyShareLink">
            <BaseIcon name="link" size="14" />
            {{ $t('form_card.copy_share_link') }}
          </div>
          <div
            class="dropdown-menu__item dropdown-menu__item--danger"
            @click.stop="deleteForm"
            v-if="isAdmin"
          >
            {{ $t('form_card.delete_funnel') }}
          </div>
        </template>
      </DropdownMenu>
      <!-- <Btn
        text="Edit flow"
        @click="router.push(`/flow/${form.id}`)"
        variant="secondary"
        :disabled="!isAdmin"
      />
      <BaseIcon
        name="trash"
        @click="deleteForm"
        size="12"
        class="form-card__delete"
        v-if="isAdmin"
      /> -->
    </div>
  </div>
</template>

<script>
  import { useRoleCheck } from '@/composables/useRoleCheck';
  import { computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { useFormStore } from '@/store/formStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import useDayJs from '@/utils/useDayJs';
  import { formatDate } from '@/utils/date';
  import { useI18n } from '@/composables/useI18n';
  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import DropdownMenu from '@/components/partials/DropdownMenu.vue';
  import Label from '@/components/partials/Label.vue';
  import OpenFunnelModal from '@/components/dashboard/OpenFunnelModal.vue';
  import { useUserStore } from '@/store/userStore';
  import { getFormUrl } from '@/utils/urlHelper';
  export default {
    name: 'FormCard',
    components: {
      Btn,
      BaseIcon,
      DropdownMenu,
      Label,
    },
    props: {
      form: {
        type: Object,
      },
    },
    setup(props) {
      const router = useRouter();
      const formStore = useFormStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();
      const userStore = useUserStore();
      const { isAdmin } = useRoleCheck();
      const { t } = useI18n();

      const isConfigurator = computed(() => props.form?.type === 'configurator');

      const openFunnelMode = (mode) => {
        const baseUrl = getFormUrl(userStore.currentOrganization, props.form.id);
        const url = mode === 'extended' ? `${baseUrl}?mode=extended` : baseUrl;
        window.open(url, '_blank');
      };

      const openFunnel = () => {
        const modalId = uiStore.addModal(OpenFunnelModal, {
          title: t('form_card.open_funnel_title'),
          description: t('form_card.open_funnel_description'),
          size: 'sm',
          onSelect: (mode) => {
            uiStore.closeModal(modalId);
            openFunnelMode(mode);
          },
        });
      };

      const deleteForm = () => {
        const modalId = uiStore.addModal(null, {
          title: t('form_card.delete_flow_title'),
          description: t('form_card.delete_flow_description'),
          status: 'danger',
          onSubmit: async () => {
            try {
              await formStore.deleteForm(props.form.id);
            } catch (error) {
              if (error.response?.data?.code !== 'FORM_HAS_BOOKINGS') throw error;
              toastStore.addToast({
                message: t('form_card.delete_blocked_by_bookings'),
                type: 'error',
              });
            }
            uiStore.closeModal(modalId);
          },
        });
      };

      const copyShareLink = () => {
        const shareLink = getFormUrl(userStore.currentOrganization, props.form.id);
        navigator.clipboard.writeText(shareLink);
        toastStore.addToast({
          message: t('form_card.share_link_copied'),
          type: 'success',
        });
      };

      return {
        router,
        deleteForm,
        formatDate,
        isAdmin,
        copyShareLink,
        isConfigurator,
        openFunnel,
        openFunnelMode,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/mixins';
  .form-card {
    position: relative;
    cursor: pointer;
    box-shadow:
      0 0 #0000,
      0 0 #0000,
      0 1px 2px 0 rgb(0 0 0 / 0.05);
    background-color: #ffffff;
    padding: 1rem 1.5rem 1rem 1rem;
    border-radius: 0.5rem;
    border: 1px solid var(--slate-200);
    @include lg-down {
      padding: 0.75rem 1.5rem 0.75rem 0.75rem;
    }
    @include hover {
      box-shadow:
        0 0 #0000,
        0 0 #0000,
        0 1px 2px 0 rgb(0 0 0 / 0.05);
      border: 1px solid var(--slate-200);
    }
    &__title {
      font-weight: 600;
      font-size: 1.25rem;
      margin-bottom: 0.5rem;
    }
    &__subtitle {
      color: var(--slate-500);
    }
    &__type {
      margin-top: 0.75rem;
    }
    &__actions {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-top: 0.75rem;
      position: absolute;
      top: 0.5rem;
      right: 1rem;
    }
    &__delete {
      color: var(--slate-500);
      cursor: pointer;
      @include hover {
        color: var(--red-500);
      }
    }
  }
</style>
